
import type { UserAnswers, AiRecommendation, Wine } from '../types';
import { logAndRethrowAiError } from "../utils/errorUtils";
import { backendRecommend, backendCharacteristics, backendExtractWineFromImage } from "./backendAiClient";

const API_TIMEOUT = 60000; // 60 seconds
const DEBUG_MODE = true; // デバッグモード
const MAX_RETRIES = 3; // 最大リトライ回数
const RETRY_DELAY = 2000; // リトライ間隔（ミリ秒）

/**
 * 防御的キャスト: 入力を必ずWine配列に変換
 */
function toWineArray(input: unknown): Wine[] {
  if (Array.isArray(input)) {
    return input as Wine[];
  }
  if (input && typeof input === 'object') {
    return [input as Wine];
  }
  return [];
}

/** Cloud Run経由でVertex AIを呼び出す（ブラウザからGemini直接呼び出しはしない） */
async function generateJSON({
  model = "gemini-2.5-flash",
  input,
  schema,
  wines,
}: {
  model?: string;
  input: string;
  schema?: any;
  wines?: any[];
}): Promise<string> {
  void model;
  void schema;

  const winesArray = toWineArray(wines);
  if (winesArray.length === 0) {
    throw new Error('Bad request: wines must be a non-empty array');
  }

  const maxRetries = 3;
  let lastError: any;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const data = await backendRecommend(winesArray, input);
      const list = Array.isArray(data.list) ? data.list : [];
      const valid = list.length > 0 && list.every((it: any) => it?.name && it?.reason);
      if (!valid) {
        throw new Error("AIが有効な推薦リストを返しませんでした。");
      }
      return JSON.stringify(list);
    } catch (error: any) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        continue;
      }
      throw error;
    }
  }

  throw lastError || new Error('リトライが失敗しました');
}

function extractJsonString(text: string): string {
  const match = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  return match ? match[1].trim() : text.trim();
}

/**
 * 画像からワインデータを抽出（Cloud Run経由）
 */
export async function extractWineDataFromImage(imageBase64: string): Promise<Partial<import('../types').Wine>> {
  console.log('📸 画像からワインデータ抽出開始（backend）');
  const data = await backendExtractWineFromImage(imageBase64);
  const wineData = data.wine || {};

  const normalizedData: Partial<import('../types').Wine> = {
    name: wineData.name || '',
    type: (wineData.type === '赤' || wineData.type === '白' || wineData.type === 'ロゼ' || wineData.type === 'スパークリング')
      ? wineData.type
      : '赤',
    origin: wineData.origin || '',
    variety: wineData.variety || '',
    price: typeof wineData.price === 'number' ? wineData.price : 0,
    taste: (['フルボディ', 'ミディアムボディ', 'ライトボディ', '辛口', '甘口', 'やや甘口'].includes(wineData.taste))
      ? wineData.taste
      : 'ミディアムボディ',
    characteristics: [],
    visible: true,
    image: imageBase64,
  };

  if (wineData.isGlassWine !== undefined) {
    normalizedData.isGlassWine = wineData.isGlassWine;
  }
  if (wineData.glassPrice !== undefined && typeof wineData.glassPrice === 'number') {
    normalizedData.glassPrice = wineData.glassPrice;
  }

  return normalizedData;
}

/**
 * 特徴語抽出（Cloud Run経由）
 */
export async function getWineCharacteristicsV2(
  wine: { id: string | number; name: string; origin: string; variety: string; type?: string; taste?: string }
): Promise<import('../types').Characteristic[]> {
  if (!wine.name?.trim() || !wine.origin?.trim() || !wine.variety?.trim()) {
    console.warn('⚠️ 特徴語抽出: 必須項目が不足しています');
    return [];
  }

  try {
    console.log('🍷 特徴語抽出開始（backend）:', wine.name);
    const data = await backendCharacteristics(wine);
    const now = Date.now();
    const characteristics = (data.characteristics || []).map((c: any) => ({
      id: crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).substring(2)}`,
      label: c.label,
      detail: c.detail,
      weight: c.weight,
      confidence: c.confidence,
      source: 'ai' as const,
      locked: false,
      createdAt: now,
      updatedAt: now,
    })).filter((c: any) => typeof c.label === 'string' && c.label.trim());

    console.log(`✅ 特徴語抽出完了: ${characteristics.length}件`);
    return characteristics;
  } catch (error) {
    console.error('❌ 特徴語抽出エラー:', error);
    return [];
  }
}

// V1関数は削除されました。getWineCharacteristicsV2() を使用してください。

/**
 * 価格帯判定関数
 */
function isPriceInRange(price: number, priceRange: string): boolean {
  switch (priceRange) {
    case '～¥5,000':
      return price <= 5000;
    case '¥5,001～¥8,000':
      return price > 5000 && price <= 8000;
    case '¥8,001～':
      return price > 8000;
    default:
      return true; // 指定なしの場合は全て対象
  }
}

/**
 * 事前フィルタリング関数
 */
function preFilterWines(wineList: Wine[], answers: UserAnswers): Wine[] {
  console.log('=== 事前フィルタリング開始 ===');
  console.log('元のワイン数:', wineList.length);
  console.log('ユーザー回答:', answers);
  
  // 元のワインリストの詳細をログ出力
  console.log('元のワインリスト:', wineList.map(w => ({
    id: w.id,
    name: w.name,
    type: w.type,
    price: w.price,
    origin: w.origin,
    taste: w.taste,
    visible: w.visible
  })));
  
  let filtered = wineList.filter(w => w.visible);
  console.log('表示対象ワイン数:', filtered.length);
  
  // ワインタイプ（色）でフィルタリング
  if (answers.color && answers.color !== '指定なし') {
    const beforeCount = filtered.length;
    filtered = filtered.filter(wine => wine.type === answers.color);
    console.log(`タイプフィルタ (${answers.color}): ${beforeCount} → ${filtered.length}`);
    if (filtered.length > 0) {
      console.log('タイプフィルタ後のワイン:', filtered.map(w => ({ id: w.id, name: w.name, type: w.type })));
    }
  }
  
  // 価格帯でフィルタリング
  if (answers.price && answers.price !== '指定なし') {
    const beforeCount = filtered.length;
    filtered = filtered.filter(wine => isPriceInRange(wine.price, answers.price));
    console.log(`価格フィルタ (${answers.price}): ${beforeCount} → ${filtered.length}`);
    if (filtered.length > 0) {
      console.log('価格フィルタ後のワイン:', filtered.map(w => ({ id: w.id, name: w.name, price: w.price })));
    } else {
      console.log('価格フィルタで除外されたワイン:', wineList.filter(w => w.visible).map(w => ({ 
        id: w.id, 
        name: w.name, 
        price: w.price, 
        inRange: isPriceInRange(w.price, answers.price) 
      })));
    }
  }
  
  // 産地でフィルタリング（部分一致）
  if (answers.origin && answers.origin !== '指定なし') {
    const beforeCount = filtered.length;
    filtered = filtered.filter(wine => wine.origin.includes(answers.origin));
    console.log(`産地フィルタ (${answers.origin}): ${beforeCount} → ${filtered.length}`);
    if (filtered.length > 0) {
      console.log('産地フィルタ後のワイン:', filtered.map(w => ({ id: w.id, name: w.name, origin: w.origin })));
    }
  }
  
  // 味わいでフィルタリング
  if (answers.taste && answers.taste !== '指定なし') {
    const beforeCount = filtered.length;
    filtered = filtered.filter(wine => wine.taste === answers.taste);
    console.log(`味わいフィルタ (${answers.taste}): ${beforeCount} → ${filtered.length}`);
    if (filtered.length > 0) {
      console.log('味わいフィルタ後のワイン:', filtered.map(w => ({ id: w.id, name: w.name, taste: w.taste })));
    }
  }
  
  // 結果が0件の場合は条件を緩める
  if (filtered.length === 0) {
    console.log('フィルタ結果が0件のため、条件を緩和します');
    filtered = wineList.filter(w => w.visible);
    
    // タイプのみで再フィルタ
    if (answers.color && answers.color !== '指定なし') {
      filtered = filtered.filter(wine => wine.type === answers.color);
      console.log(`緩和後のタイプフィルタ (${answers.color}): ${filtered.length}件`);
    }
  }
  
  // 最大60本に制限（事前絞り込み後も多い場合の上限）
  if (filtered.length > 60) {
    console.log(`候補が多すぎるため60本に制限: ${filtered.length} → 60`);
    filtered = filtered.slice(0, 60);
  }
  
  console.log('=== 事前フィルタリング完了 ===');
  console.log('最終候補数:', filtered.length);
  console.log('削減率:', Math.round((1 - filtered.length / wineList.length) * 100) + '%');
  console.log('最終候補ワイン:', filtered.map(w => ({ id: w.id, name: w.name, type: w.type, price: w.price })));
  
  return filtered;
}

/**
 * ワイン推薦（Gemini API経由）
 */
export async function getWineRecommendation(
  answers: UserAnswers,
  wineList: Wine[]
): Promise<AiRecommendation[]> {
  // 事前フィルタリングを実行
  const filteredWines = preFilterWines(wineList, answers);
  
  if (filteredWines.length === 0) {
    console.log('フィルタリング後の候補が0件です');
    return [];
  }
  
  const listForPrompt = filteredWines.map(({ image, visible, characteristics, ...rest }) => ({
    id: rest.id,
    name: rest.name,
    type: rest.type,
    origin: rest.origin,
    variety: rest.variety,
    taste: rest.taste,
    price: rest.price,
  }));

  // サーバー側でワイン一覧を組み立てるため、条件だけを短く送る（ワイン一覧の二重送信を避ける）
  const userConditions = [
    `シーン: ${answers.occasion}`,
    `料理: ${answers.food || '指定なし'}`,
    `色: ${answers.color}`,
    `味わい: ${answers.taste}`,
    `予算: ${answers.price}`,
    `産地: ${answers.origin}`,
  ].join('\n');

  console.log('=== ワイン推薦リクエスト ===');
  console.log('ワイン候補数:', listForPrompt.length);
  console.log('ユーザー条件:', userConditions);

  try {
    console.log('=== AI API呼び出し開始 ===');
    const startTime = Date.now();
    
    const rawResponse = await generateJSON({
      model: "gemini-2.5-flash",
      input: userConditions,
      wines: listForPrompt,
    });
    
    const endTime = Date.now();
    const responseTime = endTime - startTime;
    console.log(`=== AI API呼び出し完了 === 応答時間: ${responseTime}ms`);
    console.log('=== AIからの生レスポンス詳細 ===');
    console.log('生レスポンスの型:', typeof rawResponse);
    console.log('生レスポンスの長さ:', rawResponse ? rawResponse.length : 0);
    console.log('生レスポンスの内容:', rawResponse);
    console.log('=== 生レスポンス終了 ===');
    
    const jsonText = extractJsonString(rawResponse);
    console.log('=== JSON抽出後 ===');
    console.log('抽出後のテキストの型:', typeof jsonText);
    console.log('抽出後のテキストの長さ:', jsonText ? jsonText.length : 0);
    console.log('抽出後のテキストの内容:', jsonText);
    console.log('=== JSON抽出終了 ===');
    
    if (!jsonText || jsonText.trim() === '') {
      console.error('AIからの応答が空です');
      throw new Error('AIからの応答が空でした。');
    }
    
    let result;
    try {
      result = JSON.parse(jsonText || "[]");
      console.log('JSON解析成功:', result);
    } catch (parseError) {
      console.error('JSON解析エラー:', parseError);
      console.error('解析しようとした文字列:', jsonText);
      throw new Error(`AIからの応答のJSON解析に失敗しました: ${parseError}`);
    }

    if (!Array.isArray(result)) {
      console.warn('AI response was not a valid list:', result);
      console.warn('結果の型:', typeof result);
      console.warn('結果の値:', result);
      throw new Error('AIが有効な推薦リストを返しませんでした。');
    }
    
    console.log('推薦結果数:', result.length);
    console.log('推薦結果詳細:', result);
    
    // デバッグ用：利用可能なワインを確認
    console.log('=== 利用可能なワイン ===');
    console.log(listForPrompt.map(w => ({ id: w.id, name: w.name })));
    console.log('========================');
    
    // 名前マッチングによる検証とID補完
    const validatedResult = result
      .map(rec => {
        console.log(`推薦を検証中: "${rec.name}"`);
        
        // 1. まず ID で検索（もしあれば）
        let wine = listForPrompt.find(w => w.id === rec.id);
        
        // 2. ID がなければ名前で完全一致検索
        if (!wine && rec.name) {
          wine = listForPrompt.find(w => w.name === rec.name);
          
          // 3. 完全一致がなければ部分一致検索
          if (!wine) {
            wine = listForPrompt.find(w => 
              w.name.includes(rec.name) || rec.name.includes(w.name)
            );
          }
          
          // 4. さらに柔軟なマッチング（スペース、記号を無視）
          if (!wine) {
            const normalize = (str: string) => str.replace(/[\s　・\-－]/g, '').toLowerCase();
            const normalizedRecName = normalize(rec.name);
            wine = listForPrompt.find(w => {
              const normalizedWineName = normalize(w.name);
              return normalizedWineName.includes(normalizedRecName) || 
                     normalizedRecName.includes(normalizedWineName);
            });
          }
          
          if (wine) {
            console.log(`✅ 名前マッチング成功: "${rec.name}" → ID ${wine.id} (${wine.name})`);
            rec.id = wine.id; // ID を補完
          } else {
            console.warn(`❌ マッチング失敗: "${rec.name}" はワインリストに見つかりませんでした`);
          }
        } else if (wine) {
          console.log(`✅ ID マッチング成功: ID ${rec.id}`);
        }
        
        return wine ? { ...rec, id: wine.id } : null;
      })
      .filter((rec): rec is AiRecommendation => rec !== null);
    
    console.log('検証後の推薦数:', validatedResult.length);
    console.log('最終推薦結果:', validatedResult);

    return validatedResult;
    
  } catch (e: any) {
    console.error('=== ワイン推薦エラー詳細 ===');
    console.error('エラーの型:', typeof e);
    console.error('エラーの内容:', e);
    console.error('エラーのメッセージ:', e.message);
    console.error('エラーのスタック:', e.stack);
    logAndRethrowAiError(e, 'getWineRecommendation');
  }
}
